import React, { useState, useEffect } from 'react';
import { X, Send, User, Mail, Loader2, Sparkles } from 'lucide-react';
import { API_URL } from './constants';

const WaitingListPopup = ({ isOpen, setIsOpen, pro, toast }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  // Handle animation visibility
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    } else {
      const timer = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!visible && !isOpen) return null;

  const togglePopup = () => {
    setIsOpen(false);
    setTimeout(() => {
      setName("");
      setEmail("");
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const params = {
        "email_address": email,
        "user_name": name,
        "pro_or_enterprise": pro
      };

      const response = await fetch(`${API_URL}/api/join-waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success("Thank you! We'll be in touch shortly.");
      togglePopup();
    } catch (error) {
      console.error('Error calling API:', error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center p-4 transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px] transition-all duration-300"
        onClick={togglePopup}
      />

      {/* Modal Container - Made smaller (max-w-xs) */}
      <div
        className={`
          relative w-full max-w-xs bg-white rounded-xl shadow-2xl 
          transform transition-all duration-300 ease-out
          ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'} 
          overflow-hidden
        `}
      >

        {/* Header - Solid Blue-600 to match page */}
        <div className="absolute top-0 left-0 w-full h-24 bg-blue-600">
          {/* Subtle texture/circles */}
          <div className="absolute top-[-50%] right-[-20%] w-32 h-32 bg-white/10 rounded-full blur-xl" />
        </div>

        {/* Close Button */}
        <button
          onClick={togglePopup}
          className="absolute top-2 right-2 z-20 p-1 bg-black/5 hover:bg-black/10 text-white/90 rounded-full transition-all"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative pt-4 px-5 pb-5">

          {/* Header Content */}
          <div className="text-center mb-4 relative z-10">
            <div className="mx-auto w-10 h-10 rounded-lg flex items-center justify-center mb-2 shadow-md bg-white/20 backdrop-blur-sm ring-2 ring-white/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>

            <h2 className="text-lg font-bold text-white tracking-tight leading-tight">
              {pro ? pro : "Get in Touch"}
            </h2>
            <p className="text-blue-50 text-[11px] mt-1 font-medium leading-tight">
              Join the list for early access.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-lg shadow-lg p-4 ring-1 ring-black/5 relative z-10">
            <form onSubmit={handleSubmit} className="space-y-3">

              <div className="space-y-1">
                <label htmlFor="name" className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-0.5">
                  Name
                </label>
                <div className="relative group">
                  <User className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Your Name"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="email" className="text-[10px] font-bold text-gray-500 uppercase tracking-wider ml-0.5">
                  Email
                </label>
                <div className="relative group">
                  <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="name@email.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 text-white font-semibold py-2.5 rounded-md shadow-md hover:shadow-lg transform active:scale-[98%] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-xs"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <span>Join Now</span>
                    <Send className="w-3 h-3" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitingListPopup;